import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Helmet } from 'react-helmet';
import withWidth from '@material-ui/core/withWidth';

import { getFormValues } from 'redux-form';

import Layout from 'components/common/Layout';
import Progress from 'components/common/Progress';
import Filter from 'components/common/Filter';
import TabNavigation from 'components/common/TabNavigation';
import DiagnosesTable from 'components/settings/DiagnosesTable';
import DiagnosisCreateEditDialog from 'components/settings/DiagnosisCreateEditDialog';
import UploadDialog from 'components/settings/UploadDialog';

import {
  fetchDiagnoses,
  clearDiagnoses,
} from 'redux/modules/diagnoses';

import { getFilterChangeTypes } from 'utils/filterChangeTypes';
import objectDiff from 'utils/objectDiff';
import throttle from 'utils/throttle';

import { appTitle, titleSeparator } from 'config/helmetSettings';

class DataPortabilityDiagnoses extends Component {
  constructor(props) {
    super(props);

    this.state = {
      createDialogOpen: false,
      uploadDialogOpen: false,
      mobileFilterExpanded: ['xs', 'sm'].indexOf(props.width) === -1,
      limit: 10,
      offset: 0,
    };

    this.onRequest = this.onRequest.bind(this);
    this.onFilterPrimaryButtonClick = this.onFilterPrimaryButtonClick.bind(this);
    this.onFilterSecondaryButtonClick = this.onFilterSecondaryButtonClick.bind(this);
    this.onCreateDialogClose = this.onCreateDialogClose.bind(this);
    this.onUploadDialogClose = this.onUploadDialogClose.bind(this);
    this.onMobileFilterBottonClick = this.onMobileFilterBottonClick.bind(this);
    this.onTableScroll = this.onTableScroll.bind(this);

    this.onThrottledRequest = throttle(this.onRequest, 2000, false, true);
  }

  componentWillMount() {
    this.props.actions.clearDiagnoses();
  }

  componentDidMount() {
    this.onRequest();
  }

  componentWillReceiveProps(nextProps) {
    const { filters, width } = this.props;
    const { limit, offset } = this.state;
    const filtersChanged = nextProps.filters['DX Code / Long Description']
      !== filters['DX Code / Long Description'];
    let newOffset = offset;
    if (filtersChanged) {
      newOffset = 0;
    }
    if (newOffset !== offset) {
      this.setState({ offset: newOffset });
    }

    if (nextProps.width !== width) {
      this.setState({
        mobileFilterExpanded: ['xs', 'sm'].indexOf(nextProps.width) === -1,
      });
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    return ((Object.keys(objectDiff(this.props, nextProps)).length +
      Object.keys(objectDiff(this.state, nextState)).length) > 0);
  }

  componentDidUpdate(prevProps, prevState) {
    const { filters, diagnoses, count, loading } = this.props;
    const { limit, offset } = this.state;
    const limitChanged = prevState.limit !== limit;
    const offsetChanged = prevState.offset !== offset;
    const { throttledFiltersChanged, filtersChanged } = getFilterChangeTypes(
      prevProps.filters,
      filters,
      {
        textFilterKeys: ['DX Code / Long Description'],
      },
    );

    if (throttledFiltersChanged) {
      this.onThrottledRequest();
    } else if (limitChanged || offsetChanged || filtersChanged) {
      this.onRequest();
    } else if (diagnoses.length < count
      && this.scrollRef
      && this.scrollRef.scrollTop === 0
      && loading === 0) {
      this.onTableScroll({ target: this.scrollRef });
    }
  }

  onRequest() {
    const { actions, filters } = this.props;
    const { limit, offset } = this.state;
    const requestFilters = filters && Object.keys(filters).length > 0
      ? Object.keys(filters).map(key => ({
        ...filters[key].length >= 2 && /^[A-Z]+\d/i.test(filters[key])
          ? {
            name: 'DX Code',
            operation: 'startswith',
          } : {
            name: 'Long Description',
            operation: 'contains',
          },
        val: filters[key],
        case_sensitivity: false,
      }))
      : null;
    actions.fetchDiagnoses({
      limit,
      offset,
      filters: requestFilters,
      triggerLoading: true,
      push: offset > 0,
    });
  }

  onFilterPrimaryButtonClick() {
    this.setState({ createDialogOpen: true });
  }

  onCreateDialogClose() {
    this.setState({ createDialogOpen: false });
  }

  onFilterSecondaryButtonClick() {
    this.setState({ uploadDialogOpen: true });
  }

  onUploadDialogClose() {
    this.setState({ uploadDialogOpen: false });
  }

  onMobileFilterBottonClick() {
    this.setState({ mobileFilterExpanded: !this.state.mobileFilterExpanded });
  }

  onTableScroll(event) {
    const { offset, limit } = this.state;

    if ((event.target.offsetHeight + event.target.scrollTop) / event.target.scrollHeight > 0.9) {
      if ((this.props.diagnoses.length - limit) >= offset) {
        this.setState({
          offset: offset + limit,
        });
      }
    }
  }

  render() {
    const {
      diagnoses,
      count,
      loading,
      account,
      filters,
    } = this.props;
    const {
      createDialogOpen,
      uploadDialogOpen,
      mobileFilterExpanded,
    } = this.state;
    return (
      <Layout variant="settings">
        <Helmet>
          <title>
            {appTitle} {titleSeparator} Data Portability {titleSeparator} Diagnosis
          </title>
        </Helmet>
        <TabNavigation
          items={[
            {
              text: 'Referrals',
              href: '/settings/medical-sources/referrals',
            },
            {
              text: 'Sales',
              href: '/settings/medical-sources/sales',
            },

            {
              text: 'Prescribers',
              href: '/settings/medical-sources/prescribers',
            },
            {
              text: 'HCPCS Codes',
              href: '/settings/medical-sources/hcpc',
            },
            {
              text: 'Diagnosis',
              href: '/settings/medical-sources/diagnosis',
            },
            {
              text: 'Payers',
              href: '/settings/medical-sources/payers',
            },
          ]}
        />
        <Filter
          form="diagnosesFilter"
          placeholder="Search by DX Code or Description"
          primaryButtonText="Add New"
          onPrimaryButtonClick={this.onFilterPrimaryButtonClick}
          secondaryButtonText="Upload File"
          onSecondaryButtonClick={this.onFilterSecondaryButtonClick}
          secondaryButtonDisabled={account.Roles.filter(item =>
            item.Name === 'Master Administrator' || item.Name === 'Administrator').length === 0}
          onMobileFilterBottonClick={this.onMobileFilterBottonClick}
          mobileFilterExpanded={mobileFilterExpanded}
          filters={filters}
          showTableCount
          count={count}
          loading={loading}
          items={diagnoses}
          beforeTable
        />
        {loading > 0 &&
          <Progress fixed />
        }
        <DiagnosesTable
          items={diagnoses}
          count={count}
          loading={loading}
          mobileFilterExpanded={mobileFilterExpanded}
          onScroll={this.onTableScroll}
          scrollRef={(scrollRef) => { this.scrollRef = scrollRef; }}
        />
        <DiagnosisCreateEditDialog
          form="DiagnosisCreate"
          open={createDialogOpen}
          onClose={this.onCreateDialogClose}
          onSubmitSuccess={() => {
            this.onCreateDialogClose();
            this.onRequest();
          }}
        />
        <UploadDialog
          title="Upload Diagnoses"
          subtitle="To upload list of Diagnosis codes, use the template provided below:"
          route="diagnosis"
          form="diagnosesUpload"
          open={uploadDialogOpen}
          onClose={this.onUploadDialogClose}
          onSubmitSuccess={() => {
            this.onUploadDialogClose();
            this.onRequest();
          }}
        />
      </Layout>
    );
  }
}

DataPortabilityDiagnoses.propTypes = {
  diagnoses: PropTypes.arrayOf(PropTypes.shape({
    ID: PropTypes.number,
  })).isRequired,
  count: PropTypes.number.isRequired,
  loading: PropTypes.number.isRequired,
  filters: PropTypes.shape({
    'DX Code / Long Description': PropTypes.string,
  }).isRequired,
  account: PropTypes.shape({
    Roles: PropTypes.arrayOf(PropTypes.shape({
      Name: PropTypes.string.isRequired,
    })).isRequired,
  }).isRequired,
  actions: PropTypes.shape({
    fetchDiagnoses: PropTypes.func.isRequired,
    clearDiagnoses: PropTypes.func.isRequired,
  }).isRequired,
  width: PropTypes.string.isRequired,
};

DataPortabilityDiagnoses.defaultProps = {};

const mapStateToProps = (store) => {
  const diagnosesFilterValues = getFormValues('diagnosesFilter')(store);
  return {
    diagnoses: store.diagnoses.data,
    count: store.diagnoses.count,
    account: store.account,
    filters: {
      ...diagnosesFilterValues
        && diagnosesFilterValues.Filter
        ? { 'DX Code / Long Description': diagnosesFilterValues.Filter }
        : {},
    },
    loading: store.diagnoses.loading,
  };
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({
    fetchDiagnoses,
    clearDiagnoses,
  }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(
  withWidth()(DataPortabilityDiagnoses),
);



// WEBPACK FOOTER //
// ./src/pages/settings/DataPortabilityDiagnoses.js